import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FaHome, FaCalendarAlt, FaImages, FaUsers, FaUserClock,
  FaBoxOpen, FaCashRegister, FaUserShield, FaShoppingBasket, FaSignOutAlt, FaEllipsisH, FaAddressBook, FaCut, FaCalendarCheck, FaFileInvoiceDollar, FaChartBar, FaBell, FaCog
} from "react-icons/fa";
import "../../styles/Topbar.css";

const menuItems = [
  { name: "Dashboard", short: "Home", path: "/dashboard", icon: FaHome, roles: ["super_admin", "manager", "cashier"] },
  { name: "Bookings", short: "Bookings", path: "/booking", icon: FaCalendarAlt, roles: ["super_admin", "manager"] },
  { name: "Walk-in", short: "Walk-in", path: "/walk-in", icon: FaShoppingBasket, roles: ["super_admin", "manager", "cashier"] },
  { name: "Gallery", short: "Gallery", path: "/gallery", icon: FaImages, roles: ["super_admin", "manager"] },
  { name: "Clients", short: "Clients", path: "/clients", icon: FaAddressBook, roles: ["super_admin", "manager", "cashier"] },
  { name: "Services", short: "Services", path: "/services", icon: FaCut, roles: ["super_admin", "manager"] },
  { name: "Schedule", short: "Schedule", path: "/schedule", icon: FaCalendarCheck, roles: ["super_admin", "manager"] },
  { name: "Staff", short: "Staff", path: "/staff", icon: FaUsers, roles: ["super_admin", "manager"] },
  { name: "Attendance", short: "Attendance", path: "/attendance", icon: FaUserClock, roles: ["super_admin", "manager"] },
  { name: "Products", short: "Products", path: "/products", icon: FaBoxOpen, roles: ["super_admin", "manager", "cashier"] },
  { name: "Sales", short: "Sales", path: "/sales", icon: FaCashRegister, roles: ["super_admin", "manager", "cashier"] },
  { name: "Expenses", short: "Expenses", path: "/expenses", icon: FaFileInvoiceDollar, roles: ["super_admin", "manager"] },
  { name: "Reports", short: "Reports", path: "/reports", icon: FaChartBar, roles: ["super_admin", "manager"] },
  { name: "Notifications & Activity", short: "Alerts", path: "/notifications", icon: FaBell, roles: ["super_admin", "manager"] },
  { name: "Settings", short: "Settings", path: "/settings", icon: FaCog, roles: ["super_admin", "manager"] },
  { name: "Admin Users", short: "Admin", path: "/admin", icon: FaUserShield, roles: ["super_admin"] },
];

export default function Topbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const allowed = menuItems.filter((item) => item.roles.includes(user?.role));
  const primaryMobile = allowed.filter(i => ["/dashboard", "/booking", "/staff", "/sales"].includes(i.path));
  const secondaryMobile = allowed.filter(i => !primaryMobile.some(p => p.path === i.path));
  const initials = (user?.username || "Admin").split(" ").map(v => v[0]).join("").slice(0, 2).toUpperCase();
  const roleLabel = (user?.role || "admin").replaceAll("_", " ");

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <>
      <aside className="admin-sidebar">
        <div className="sidebar-brand">
          <img src="/assets/logo_light.png" alt="Dey Fresh Signature" />
          <span>Salon Management</span>
        </div>

        <nav className="sidebar-nav" aria-label="Admin navigation">
          <p className="sidebar-label">Workspace</p>
          {allowed.map(({ name, path, icon: Icon }) => (
            <NavLink key={path} to={path} className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`}>
              <Icon /> <span>{name}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-note">
          <span className="sidebar-note-kicker">DEY FRESH SIGNATURE</span>
          <strong>Manage every salon day with clarity.</strong>
          <p>Bookings, team, sales and stock in one workspace.</p>
        </div>

        <div className="sidebar-user">
          <div className="sidebar-avatar">{initials}</div>
          <div className="sidebar-user-copy"><strong>{user?.username || "Admin"}</strong><span>{roleLabel}</span></div>
          <button onClick={handleLogout} title="Logout" aria-label="Logout"><FaSignOutAlt /></button>
        </div>
      </aside>

      <header className="admin-mobile-header">
        <img src="/assets/logo_light.png" alt="Dey Fresh Signature" />
        <div className="mobile-admin-avatar">{initials}</div>
      </header>

      <nav className="admin-bottom-nav" aria-label="Mobile admin navigation">
        {primaryMobile.map(({ short, path, icon: Icon }) => (
          <NavLink key={path} to={path} className={({isActive}) => `bottom-nav-link ${isActive ? "active" : ""}`}>
            <Icon /><span>{short}</span>
          </NavLink>
        ))}
        {secondaryMobile.length > 0 && (
          <button className={`bottom-nav-link ${moreOpen ? "active" : ""}`} onClick={() => setMoreOpen(v => !v)}>
            <FaEllipsisH /><span>More</span>
          </button>
        )}
      </nav>

      {moreOpen && secondaryMobile.length > 0 && (
        <div className="mobile-more-sheet" onClick={() => setMoreOpen(false)}>
          <div className="mobile-more-card" onClick={e => e.stopPropagation()}>
            <div className="more-handle" />
            <div className="more-heading"><div><span>MORE</span><strong>Salon tools</strong></div><button onClick={() => setMoreOpen(false)}>×</button></div>
            <div className="more-grid">
              {secondaryMobile.map(({ name, path, icon: Icon }) => (
                <NavLink key={path} to={path} onClick={() => setMoreOpen(false)} className="more-link"><Icon/><span>{name}</span></NavLink>
              ))}
            </div>
            <button className="mobile-logout" onClick={handleLogout}><FaSignOutAlt/> Log out</button>
          </div>
        </div>
      )}
    </>
  );
}
